// @ts-strict

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { checkDateFormat } from '@app/core/helpers/tools';
import { IPayoutFilterOption } from '@app/types/payouts';
import {
  CompareMode,
  CompareModeDateRangeType,
  CountriesStatsRequestParams,
  FilterWithCheck,
  ICountryStatistics,
  SearchInputHandler,
  StatisticsCountryFilterItem,
  Option
} from '@services/statistics/statistics.types';
import { BaseApiResponse, IChosenSortValue, ISelectedDateRangeData, PageableApiResponse } from '@app/core/models/core-models';
import { ISelectOption } from '@app/shared/types/inputs';

@Injectable({
  providedIn: 'root'
})
export class StatisticsSummaryService {
  constructor(private http: HttpClient) {}

  public dateRangeDifference = 365;
  public compareButtonInactivated = new Subject<boolean>();
  public dateRange = new Subject<any>();
  public removeOptionFromBar = new Subject<any>();
  public resetAll = new Subject<void>();
  public searchInputHandler = new Subject<SearchInputHandler | undefined>();
  public setSelectedOptions = new Subject<Record<string, Option[]>>();
  public currentSelectedOptions: Record<string, Option[]> = {};
  public isDetailsLayout = new BehaviorSubject<boolean>(false);
  public sortMethod = new Subject<any>();
  public earningsRangeSlider = new Subject<any>();
  // visibility of columns
  public username = new BehaviorSubject<boolean>(false);
  public leads = new BehaviorSubject<boolean>(false);
  public leadID = new BehaviorSubject<boolean>(false);
  public visitID = new BehaviorSubject<boolean>(false);
  public leadToPay = new BehaviorSubject<boolean>(false);
  public leadAccepted = new BehaviorSubject<boolean>(false);
  public leadPending = new BehaviorSubject<boolean>(false);
  public leadRejected = new BehaviorSubject<boolean>(false);
  public earnings = new BehaviorSubject<boolean>(false);
  public earningsToPay = new BehaviorSubject<boolean>(false);
  public earningsAccepted = new BehaviorSubject<boolean>(false);
  public earningsPending = new BehaviorSubject<boolean>(false);
  public earningsRejected = new BehaviorSubject<boolean>(false);
  public uniqueVisits = new BehaviorSubject<boolean>(false);
  public conversion = new BehaviorSubject<boolean>(false);
  public acceptance = new BehaviorSubject<boolean>(false);
  public epc = new BehaviorSubject<boolean>(false);
  public rate = new BehaviorSubject<boolean>(false);
  public countries = new BehaviorSubject<boolean>(false);
  public created = new BehaviorSubject<boolean>(false);
  public updated = new BehaviorSubject<boolean>(false);
  public sources = new BehaviorSubject<boolean>(false);
  public categories = new BehaviorSubject<boolean>(false);
  public configurationName = new BehaviorSubject<boolean>(false);
  public type = new BehaviorSubject<boolean>(false);
  public salesValue = new BehaviorSubject<boolean>(false);
  public selectedCountry = new BehaviorSubject<StatisticsCountryFilterItem[]>([]);
  public selectedCampaigns = new BehaviorSubject<ISelectOption[]>([]);
  public selectedAdminCampaigns = new BehaviorSubject<ISelectOption[]>([]);
  public refferingAddress = new BehaviorSubject<boolean>(false);
  public mlSub = new BehaviorSubject<boolean>(false);
  public status = new BehaviorSubject<boolean>(false);
  public transactionId = new BehaviorSubject<boolean>(false);
  public orderId = new BehaviorSubject<boolean>(false);
  public additionalData = new BehaviorSubject<boolean>(false);
  public cartValue = new BehaviorSubject<boolean>(false);
  public browser = new BehaviorSubject<boolean>(false);
  public browserVersion = new BehaviorSubject<boolean>(false);
  public operatingSystem = new BehaviorSubject<boolean>(false);
  public operatingSystemVersion = new BehaviorSubject<boolean>(false);
  public placeOfPromotion = new BehaviorSubject<boolean>(false);
  public registrationDate = new BehaviorSubject<boolean>(false);
  public safePage = new BehaviorSubject<boolean>(false);
  public device = new BehaviorSubject<boolean>(false);
  public onlyVoid = new Subject<void>();
  public resetFiltersOnDestroy = new Subject<void>();
  public compareMode = new Subject<CompareMode>();
  public cleanFilters = new Subject<string>();

  // payoutsfilters

  public bank = new BehaviorSubject<boolean>(true);
  public revolut = new BehaviorSubject<boolean>(true);
  public bitcoin = new BehaviorSubject<boolean>(true);
  public skrill = new BehaviorSubject<boolean>(true);
  public paypall = new BehaviorSubject<boolean>(true);
  public bill = new BehaviorSubject<boolean>(true);
  public invoice = new BehaviorSubject<boolean>(true);
  public close = new BehaviorSubject<boolean>(true);
  public during = new BehaviorSubject<boolean>(true);
  public cancel = new BehaviorSubject<boolean>(true);
  public new = new BehaviorSubject<boolean>(true);
  public amountMin = new BehaviorSubject<number>(0);
  public amountMax = new BehaviorSubject<number>(1000000);
  public tether = new BehaviorSubject<boolean>(true);
  public usdc = new BehaviorSubject<boolean>(true);
  public webmoney = new BehaviorSubject<boolean>(true);
  public epayments = new BehaviorSubject<boolean>(true);
  public payza = new BehaviorSubject<boolean>(true);
  public capitalist = new BehaviorSubject<boolean>(true);
  public umoney = new BehaviorSubject<boolean>(true);
  public qiwi = new BehaviorSubject<boolean>(true);
  public payoneer = new BehaviorSubject<boolean>(true);
  public order_placing_date = new BehaviorSubject<any>({});
  public status_change_date = new BehaviorSubject<any>({});
  public payoutsVoid = new Subject<void>();
  public sliderValues = new Subject();

  public allColumns = [
    'leads',
    'status',
    'earnings',
    'uniqueVisits',
    'conversion',
    'acceptance',
    'epc',
    'rate',
    'countries',
    'created',
    'updated',
    'sources',
    'categories',
    'configurationName',
    'type',
    'salesValue',
    'selectedCountry',
    'refferingAddress',
    'mlSub',
    'transactionId',
    'orderId',
    'additionalData',
    'cartValue',
    'browser',
    'browserVersion',
    'operatingSystem',
    'operatingSystemVersion',
    'device'
  ];

  public tilesCompareData = [
    {
      tiles: [
        {
          subcategory: 'leads',
          value: 128,
          icon: 'lead',
          results: [
            {
              id: 0,
              status: 'toPay',
              value: 60,
              icon: 'money-protection',
              ext: ''
            },
            {
              id: 1,
              status: 'accepted',
              value: 60,
              icon: 'money-protection',
              ext: ''
            },
            {
              id: 2,
              status: 'pending',
              value: 4,
              icon: 'money-time',
              ext: ''
            },
            {
              id: 3,
              status: 'rejected',
              value: 4,
              icon: 'cross-circle',
              ext: ''
            }
          ]
        }
      ]
    }
  ];

  public compareModeHandler(compareMode: boolean, dateRange: CompareModeDateRangeType): void {
    this.compareMode.next({ compareMode, dateRange });
  }

  public resetCompareMode = () => {
    this.compareModeHandler(false, {});
    this.compareButtonInactivated.next(true);
  };

  public resetCheckboxesHandler(): void {
    this.bank.next(true);
    this.revolut.next(true);
    this.bitcoin.next(true);
    this.skrill.next(true);
    this.paypall.next(true);
    this.tether.next(true);
    this.usdc.next(true);
    this.webmoney.next(true);
    this.epayments.next(true);
    this.payoneer.next(true);
    this.payza.next(true);
    this.capitalist.next(true);
    this.umoney.next(true);
    this.qiwi.next(true);
    this.bill.next(true);
    this.invoice.next(true);
    this.close.next(true);
    this.during.next(true);
    this.cancel.next(true);
    this.new.next(true);
    this.amountMin.next(0);
    this.amountMax.next(1000000);
    this.order_placing_date.next({});
    this.status_change_date.next({});
    this.payoutsVoidHandler();
  }

  public payoutsVoidHandler(): void {
    this.payoutsVoid.next();
  }

  public setDateFilter(dateFilter: { name?: any; dataRange?: ISelectedDateRangeData }): void {
    (this[dateFilter.name as keyof Partial<StatisticsSummaryService>] as BehaviorSubject<any>).next(dateFilter);
  }

  public getActiveFilters(title: string): any {
    let filters: FilterWithCheck[] = [];
    if (title === 'method') {
      filters = [
        { name: 'bank', checked: this.bank.getValue() },
        { name: 'revolut', checked: this.revolut.getValue() },
        { name: 'bitcoin', checked: this.bitcoin.getValue() },
        { name: 'skrill', checked: this.skrill.getValue() },
        { name: 'paypall', checked: this.paypall.getValue() },
        { name: 'tether', checked: this.tether.getValue() },
        { name: 'usdc', checked: this.usdc.getValue() },
        { name: 'webmoney', checked: this.webmoney.getValue() },
        { name: 'epayments', checked: this.epayments.getValue() },
        { name: 'payoneer', checked: this.payoneer.getValue() },
        { name: 'payza', checked: this.payza.getValue() },
        { name: 'capitalist', checked: this.capitalist.getValue() },
        { name: 'umoney', checked: this.umoney.getValue() },
        { name: 'qiwi', checked: this.qiwi.getValue() }
      ];
    } else if (title === 'status') {
      filters = [
        { name: 'close', checked: this.close.getValue() },
        { name: 'during', checked: this.during.getValue() },
        { name: 'cancel', checked: this.cancel.getValue() },
        { name: 'new', checked: this.new.getValue() }
      ];
    } else if (title === 'payment_type') {
      filters = [
        { name: 'bill', checked: this.bill.getValue() },
        { name: 'invoice', checked: this.invoice.getValue() }
      ];
    }
    const activeFilters: string[] = [];

    if (filters.every(({ checked }) => checked)) {
      filters.map(({ name }) => activeFilters.push(name));

      return activeFilters;
    }

    filters.map(el => {
      if (el.checked === false) {
        activeFilters.push(el.name);
      }
    });

    return activeFilters;
  }

  public setAmountRange(val: { min?: number; max?: number }): void {
    this.amountMin.next(val.min || 0);
    this.amountMax.next(val.max || 0);
  }

  public setPayoutsFilters(filter: IPayoutFilterOption): void {
    (this[filter.name as keyof Partial<StatisticsSummaryService>] as BehaviorSubject<boolean>).next(!filter.checked);
  }

  public disableThisFilter(thisFilter: string): any {
    (this[thisFilter as keyof Partial<StatisticsSummaryService>] as BehaviorSubject<boolean>).next(true);
  }

  public setEarningsRangeSliderValues(val: number[]): void {
    this.earningsRangeSlider.next(val);
  }

  public setCampaignRangeSliderValues(rangeVal: number[], rangeName: string): void {
    this.sliderValues.next({ name: rangeName, values: rangeVal });
  }

  public setSelectedCountry(countries: StatisticsCountryFilterItem[]): void {
    this.selectedCountry.next(countries);
  }

  public setSelectedCampaigns(campaigns: ISelectOption[]): void {
    this.selectedCampaigns.next(campaigns);
  }

  public setSelectedAdminCampaigns(campaigns: ISelectOption[]): void {
    this.selectedAdminCampaigns.next(campaigns);
  }

  public isDetailsLayoutHandler(value: boolean): void {
    this.isDetailsLayout.next(value);
  }

  public onlyVoidHandler(): void {
    this.onlyVoid.next();
  }

  public setColumnsVisibility(item: { column: string; checked: boolean }): void {
    (this[item.column as keyof Partial<StatisticsSummaryService>] as BehaviorSubject<boolean>)?.next(item.checked);
    this.onlyVoid.next();
  }

  public setSortMethod(val: { sortMethod: IChosenSortValue; origin: string }): void {
    this.sortMethod.next(val);
  }

  public campaignNameHandler(menuName: string, input: string): void {
    const name = `${menuName}Input`;
    (this[name as keyof Partial<StatisticsSummaryService>] as BehaviorSubject<string>).next(input);
  }

  public voidResetFiltersOnDestroy(): void {
    this.resetFiltersOnDestroy.next();
  }

  public getCountriesStatsData(reqParams: CountriesStatsRequestParams): Observable<PageableApiResponse<{ countries: ICountryStatistics[] }>> {
    reqParams.date_from = checkDateFormat(reqParams.date_from);
    reqParams.date_to = checkDateFormat(reqParams.date_to);
    const params = { ...reqParams };
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      params
    };

    return this.http.get<PageableApiResponse<{ countries: ICountryStatistics[] }>>(environment.apiUrl + `/statistics/countries`, httpOptions);
  }

  public getIncentCampaignsList() {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    return this.http.get<BaseApiResponse<{ campaigns: { id: number; name: string }[] }>>(environment.apiUrl + `/campaigns/incent-campaign-list`, httpOptions);
  }

  public getSpecialCampaignsList(listName: string | undefined) {
    const params = {
      type: listName || 'incent'
    };
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      params
    };
    return this.http.get<BaseApiResponse<{ campaigns: { id: number; name: string }[] }>>(environment.apiUrl + `/campaigns/user-campaign-list`, httpOptions);
  }
}
